import { TrendingUp, Users, Target, Quote } from "lucide-react";
import { useCounterUp } from "@/hooks/useCounterUp";
import CursorGlow from "@/components/landing/CursorGlow";

const STATS = [
  {
    id: "roi",
    icon: TrendingUp,
    target: 312,
    prefix: "+",
    suffix: "%",
    decimals: 0,
    label: "Avg. campaign ROI",
    sub: "Across 40+ performance accounts (FY24-25)",
  },
  {
    id: "leads",
    icon: Users,
    target: 18400,
    prefix: "",
    suffix: "+",
    decimals: 0,
    label: "Qualified leads delivered",
    sub: "For real estate & lifestyle brands",
  },
  {
    id: "cac",
    icon: Target,
    target: 47,
    prefix: "-",
    suffix: "%",
    decimals: 0,
    label: "Reduction in CAC",
    sub: "Within 90 days of engagement",
  },
];

const CASES = [
  {
    id: "km-builders",
    badge: "Real Estate",
    title: "KM Builders — Pre-Launch Sellout",
    body:
      "Engineered an integrated launch funnel combining OOH, performance ads, and a premium print booklet. Generated 4,200 qualified site-visits and sold out Phase 1 inventory in 38 days.",
    metrics: [
      { k: "Leads", v: "4,200+" },
      { k: "Sold-out", v: "38 days" },
      { k: "CPL", v: "₹382" },
    ],
  },
  {
    id: "ora-makers",
    badge: "Lifestyle",
    title: "Ora Makers — Brand Repositioning",
    body:
      "Rebuilt the brand identity, brand films, and a 12-month social engineering program. Organic reach grew 6× and DTC revenue lifted 2.4× year-on-year.",
    metrics: [
      { k: "Reach", v: "6×" },
      { k: "Revenue", v: "+140%" },
      { k: "Engagement", v: "11.8%" },
    ],
  },
];

const QUOTES = [
  {
    id: "q1",
    quote:
      "Dynamic X Media didn't run campaigns — they ran an operating system. Our pre-launch sold out before brokers got involved.",
    name: "Rohan Mehta",
    title: "Director, KM Builders",
    initials: "RM",
  },
  {
    id: "q2",
    quote:
      "The level of craft across digital, brand films, and print is something we couldn't find anywhere else. Every asset feels intentional.",
    name: "Aanya Kapoor",
    title: "Founder, Ora Makers",
    initials: "AK",
  },
  {
    id: "q3",
    quote:
      "Audit-grade reporting and zero excuses. We finally trust the marketing line in our P&L.",
    name: "Vikram Shah",
    title: "CFO, Zenith House",
    initials: "VS",
  },
];

function formatStat(value, prefix, suffix) {
  const intVal = Math.round(value);
  const formatted = intVal.toLocaleString("en-IN");
  return `${prefix}${formatted}${suffix}`;
}

function StatCard({ s }) {
  const { ref, value } = useCounterUp(s.target, { duration: 1800 });
  const Icon = s.icon;
  return (
    <div
      ref={ref}
      data-testid={`case-stat-${s.id}`}
      className="rounded-2xl bg-white border border-black/10 p-7 sm:p-8 hover:border-[#2770FD] hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(39,112,253,0.3)] transition-all duration-500"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#2770FD]/10 border border-[#2770FD]/20 text-[#2770FD]">
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-black/50">
          Verified
        </span>
      </div>
      <p
        data-testid={`case-stat-value-${s.id}`}
        className="mt-6 font-display text-accent-blue text-4xl sm:text-5xl tabular-nums"
      >
        {formatStat(value, s.prefix, s.suffix)}
      </p>
      <p className="mt-2 text-black font-semibold">{s.label}</p>
      <p className="mt-1 text-xs text-black/55">{s.sub}</p>
    </div>
  );
}

export default function CaseStudies() {
  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative py-28 sm:py-36 bg-white border-t border-black/5 overflow-hidden"
    >
      <CursorGlow size={700} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="inline-block text-xs uppercase tracking-[0.3em] font-semibold text-[#2770FD] mb-5">
            Client Success Stories
          </span>
          <h2
            data-testid="case-studies-heading"
            className="font-display text-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Receipts, not <span className="text-accent-blue">rhetoric.</span>
          </h2>
          <p className="mt-6 text-black/70 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            Real numbers from real engagements. Reproducible playbooks — not lucky breaks.
          </p>
        </div>

        {/* Stats row */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {STATS.map((s) => (
            <StatCard key={s.id} s={s} />
          ))}
        </div>

        {/* Case study blocks */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {CASES.map((c) => (
            <article
              key={c.id}
              data-testid={`case-block-${c.id}`}
              className="group rounded-2xl bg-white border border-black/10 p-8 sm:p-10 hover:border-[#2770FD] hover:shadow-[0_20px_50px_-15px_rgba(39,112,253,0.3)] transition-all duration-500"
            >
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#2770FD] text-white text-[10px] uppercase tracking-[0.25em] font-semibold">
                {c.badge}
              </span>
              <h3 className="mt-5 font-display text-black text-2xl sm:text-3xl leading-tight">
                {c.title}
              </h3>
              <p className="mt-4 text-black/70 text-sm sm:text-base font-light leading-relaxed">
                {c.body}
              </p>
              <div className="mt-7 grid grid-cols-3 gap-3 pt-6 border-t border-black/10">
                {c.metrics.map((m) => (
                  <div key={m.k}>
                    <p className="font-display text-accent-blue text-2xl">
                      {m.v}
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-black/55 mt-1">
                      {m.k}
                    </p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        {/* Quotes */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {QUOTES.map((q) => (
            <figure
              key={q.id}
              data-testid={`testimonial-${q.id}`}
              className="rounded-2xl bg-white border border-black/10 p-7 sm:p-8 flex flex-col gap-5 hover:border-[#2770FD] hover:shadow-[0_20px_50px_-15px_rgba(39,112,253,0.25)] transition-all duration-500"
            >
              <Quote className="h-6 w-6 text-[#2770FD]" strokeWidth={1.5} />
              <blockquote className="text-black text-base font-light leading-relaxed">
                &ldquo;{q.quote}&rdquo;
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-5 mt-auto border-t border-black/10">
                <span
                  data-testid={`testimonial-avatar-${q.id}`}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#2770FD] text-white font-display text-sm ring-2 ring-[#2770FD]/15"
                >
                  {q.initials}
                </span>
                <div>
                  <p className="text-black text-sm font-semibold">{q.name}</p>
                  <p className="text-xs text-black/55">{q.title}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
